import dynamic from "next/dynamic";
import useSWRInfinite from "swr/infinite";

import { useEffect } from "react";
import InfiniteScroll from "react-swr-infinite-scroll";

import { format, parse } from "date-fns";
import numeral from "numeral";

import { Loader } from "components/customElements/loader";
import Table from "components/Table";
import useAuthentication from "hooks/useAuthentication";
import { baseFetcher } from "utils/baseFetcher";
import { dataCloudHost } from "utils/envVariables";

const AccountUpgradeForm = dynamic(
  () => import("components/AccountUpgradeForm")
);

export const shareholdersUrl = (isin: string, page: number) => {
  return `${dataCloudHost}/v4/registries/entities/${isin}?page=${page}&limit=10`;
};

interface Props {
  entity: any;
}

const Shareholder = ({ entity }: Props) => {
  const { account, isAccountLoading } = useAuthentication();

  const { data, isValidating, mutate, size, setSize } = useSWRInfinite(
    (pageIndex, previousPageData) => {
      if (previousPageData?.length === 0) return null;
      if (
        !(
          account?.isProfessionalClient ||
          account?.isInsightProvider ||
          account?.isInternalUser
        ) &&
        pageIndex + 1 > 2
      ) {
        return null;
      }
      return shareholdersUrl(entity?.isin, pageIndex + 1);
    },
    baseFetcher
  );
  const hasMore = !data?.length || data?.[size - 1]?.length > 0;

  const tableData = data?.reduce(
    (rs, shareholders) =>
      rs.concat(
        shareholders?.map?.((item: any) => ({
          ...item,
          perc_held: item.perc_held
            ? numeral(item.perc_held).format("0,0.000%")
            : "-",
          perc_change:
            item.perc_change && item.perc_change !== 0 && item.perc_change !== 1
              ? numeral(item.perc_change).format("0,0.00%")
              : "-",
          total_holding: item.total_holding
            ? numeral(item.total_holding).format("0,0")
            : "-",
          latest_report: item.latest_report
            ? format(
                parse(item.latest_report, "yyyy-MM-dd", new Date()),
                "dd MMM yyyy"
              )
            : "-",
        })) || []
      ),
    []
  );

  const tableColumns = [
    {
      key: "holder",
      title: "Name (Type)",
      dataIndex: "name",
      style: {
        maxWidth: "200px",
      },
    },
    {
      key: "percentage",
      title: "Holding",
      dataIndex: "perc_held",
      style: {},
    },
    {
      key: "shares",
      title: "Shares",
      dataIndex: "total_holding",
      style: {},
    },
    {
      key: "change",
      title: "Change",
      dataIndex: "perc_change",
      style: {},
    },
    {
      key: "latest-date",
      title: "Disclosed",
      dataIndex: "latest_report",
      style: {},
    },
  ];

  useEffect(() => {
    if (!hasMore) mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isAccountLoading) return <Loader />;

  if (!account?.canViewEntityShareholders) {
    return (
      <>
        <div className="bg-mellow-yellow rounded-xl p-[15px] text-subtle-black">
          As a {account?.accountType} holder, you do not have access to Top
          Shareholders. Upgrade now to get full access.
        </div>
        <div className="mt-[25px]">
          <AccountUpgradeForm />
        </div>
      </>
    );
  }

  return (
    <>
      <InfiniteScroll
        swr={{ data, mutate, isValidating, size, setSize }}
        isReachingEnd={!hasMore}
        endingIndicator={<div className={""}></div>}
      >
        <div className="mb-[30px] overflow-x-auto">
          <Table columns={tableColumns} dataSource={tableData || []} />
          {isValidating && <Loader />}
        </div>
      </InfiniteScroll>
    </>
  );
};

export default Shareholder;
